import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PromoVideo from '../components/PromoVideo';

const img = (id) => `https://images.unsplash.com/photo-${id}?w=100&auto=format&fit=crop&q=60`;

// Items shown in the hero phone
const SHOP_ITEMS = [
  { name: 'iPhone 18', price: 'R 31 000', img: img('1695048133142-1a20484d2569') },
  { name: 'Designer Sneakers', price: 'R 6 000', img: img('1542291026-7eec264c27ff') },
  { name: 'Wireless Headphones', price: 'R 2 500', img: img('1505740420928-5e560c06d30e') },
  { name: 'Classic Watch', price: 'R 4 200', img: img('1523275335684-37898b6baf30'), sold: true },
];

const FEATURES = [
  { icon: 'fa-store', title: 'A Shop on Your Profile', text: 'Your items sit right beneath your name, with photos, prices and a little detail, ready for anyone who visits to browse at their own pace.' },
  { icon: 'fa-layer-group', title: 'Neatly Organised', text: 'Available, sold and draft items each have their own place, with categories that keep a growing collection easy to look through.' },
  { icon: 'fa-comment-dots', title: 'Chat With Buyers', text: 'Questions about size, condition or collection are only a tap away, and customers can message you without sending a friend request.' },
  { icon: 'fa-credit-card', title: 'Card Payments', text: 'When someone is ready to buy, they can pay by card through PayFast straight from the item, turning a quick chat into a finished order.' },
  { icon: 'fa-chart-simple', title: 'Sales at a Glance', text: 'A calm overview of your paid orders and what you have received, with each buyer listed so following up always feels easy.' },
  { icon: 'fa-eye', title: 'See What They See', text: 'Preview your shop exactly as customers experience it, so you can feel confident everything looks just the way you intended.' },
];

// The hero phone: a business profile as a customer sees it
function StorefrontPhone() {
  return (
    <div className="phone-frame">
      <div className="phone-screen theme-light">
        <div className="app-header">
          <i className="fa-solid fa-arrow-left" style={{ color: '#64748B', fontSize: '0.9rem' }}></i>
          <h3 style={{ fontSize: '1rem' }}>Profile</h3>
          <i className="fa-solid fa-ellipsis" style={{ color: '#64748B', fontSize: '0.9rem' }}></i>
        </div>

        <div className="biz-phone-profile">
          <img src="/images/john-d.webp" alt="John D" className="biz-phone-avatar" width={64} height={64} />
          <h4>John D <i className="fa-solid fa-circle-check"></i></h4>
          <span>Business account</span>
          <div className="biz-phone-message"><i className="fa-regular fa-comment"></i> Message</div>
        </div>

        <div className="biz-phone-label">
          <span><i className="fa-solid fa-store"></i> Items for sale</span>
          <em>3 available</em>
        </div>

        <div className="chat-list">
          {SHOP_ITEMS.map((it) => (
            <div className="chat-item" key={it.name}>
              <div className="biz-phone-thumb" style={{ backgroundImage: `url('${it.img}')` }}></div>
              <div className="chat-info">
                <h4>{it.name}</h4>
                <p className="biz-phone-price">{it.price}</p>
              </div>
              {it.sold ? <span className="biz-phone-sold">Sold</span> : <i className="fa-solid fa-chevron-right biz-phone-arrow"></i>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function BusinessPage() {
  useEffect(() => {
    document.title = 'Yappyli for Business | Sell From Your Profile';
  }, []);

  return (
    <div className="yp">
      {/* HERO */}
      <section className="hero" id="business">
        <div className="container hero-grid">
          <div className="hero-content">
            <span className="section-tag">Yappyli for Business</span>
            <h1>A little shop that lives inside your chats.</h1>
            <p>
              Turn your profile into a storefront where customers can browse, ask questions and pay by card,
              all within the app they already chat in.
            </p>
            <div className="hero-buttons">
              <Link to="/download" className="btn btn-primary">
                <i className="fa-solid fa-store"></i> Start Selling
              </Link>
              <a href="#in-action" className="btn btn-secondary">See it in action</a>
            </div>
          </div>
          <div className="hero-mockup-wrapper">
            <StorefrontPhone />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features" id="business-features">
        <div className="container">
          <div className="text-center">
            <span className="section-tag">Why Sell on Yappyli?</span>
            <h2 className="section-title">Built around the way people already shop</h2>
            <p className="section-desc">Browsing, asking a quick question and paying, woven naturally into your conversations.</p>
          </div>

          <div className="features-grid">
            {FEATURES.map((f) => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon"><i className={`fa-solid ${f.icon}`}></i></div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IN ACTION: business promo video */}
      <section className="theme-section" id="in-action">
        <div className="container">
          <div className="text-center">
            <span className="section-tag">See It in Action</span>
            <h2 className="section-title">From first look to paid order</h2>
            <p className="section-desc">Watch customers go from browsing your catalogue to paying by card.</p>
          </div>

          <PromoVideo
            src="/videos/yappyli-business-promo.mp4"
            poster="/videos/yappyli-business-promo-poster.jpg"
            title="Yappyli for Business promo video"
          />
        </div>
      </section>

      {/* CTA */}
      <div className="container">
        <section className="cta-section">
          <h2>Ready to open your shop?</h2>
          <p>Download Yappyli, switch to a business account and share your first items today.</p>
          <div className="cta-buttons">
            <Link to="/download" className="btn btn-secondary">
              <i className="fa-solid fa-download"></i> Download Yappyli
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              <i className="fa-solid fa-envelope"></i> Talk to us
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
